import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface Category {
  id: string;
  name: string;
  description: string;
  productCount: number;
  parentId: string | null;
  color: string;
  icon: string;
  isActive: boolean;
}

const MOCK_CATEGORIES: Category[] = [
  { id: "1", name: "Electronics", description: "Electronic devices and accessories", productCount: 45, parentId: null, color: "#3B82F6", icon: "ri-cpu-line", isActive: true },
  { id: "2", name: "Smartphones", description: "Mobile phones and tablets", productCount: 23, parentId: "1", color: "#8B5CF6", icon: "ri-smartphone-line", isActive: true },
  { id: "3", name: "Accessories", description: "Electronic accessories", productCount: 67, parentId: "1", color: "#EC4899", icon: "ri-headphone-line", isActive: true },
  { id: "4", name: "Clothing", description: "Apparel and fashion", productCount: 128, parentId: null, color: "#10B981", icon: "ri-t-shirt-line", isActive: true },
  { id: "5", name: "Men's Wear", description: "Men's clothing", productCount: 56, parentId: "4", color: "#6366F1", icon: "ri-user-line", isActive: true },
  { id: "6", name: "Women's Wear", description: "Women's clothing", productCount: 72, parentId: "4", color: "#F59E0B", icon: "ri-user-3-line", isActive: true },
  { id: "7", name: "Food & Beverages", description: "Food items and drinks", productCount: 89, parentId: null, color: "#EF4444", icon: "ri-restaurant-line", isActive: false },
  { id: "8", name: "Home & Garden", description: "Home decor and garden supplies", productCount: 34, parentId: null, color: "#14B8A6", icon: "ri-home-4-line", isActive: true },
];

export default function CategoriesSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [, setSelectedCategory] = useState<Category | null>(null);

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
    tableRowHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
  };

  const filteredCategories = MOCK_CATEGORIES.filter((category) =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const parentCategories = MOCK_CATEGORIES.filter((c) => c.parentId === null);
  const getChildren = (parentId: string) => MOCK_CATEGORIES.filter((c) => c.parentId === parentId);

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${themeClasses.text}`}>{t("posProducts.categories.title")}</h1>
          <p className={themeClasses.textSecondary}>{t("posProducts.categories.subtitle")}</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 rounded-xl bg-primary-500 text-gray-900 font-medium flex items-center gap-2"
        >
          <i className="ri-add-line"></i>
          {t("posProducts.categories.addCategory")}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} overflow-hidden`}>
            <div className={`p-4 border-b ${themeClasses.border}`}>
              <div className="relative">
                <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
                <input
                  type="text"
                  placeholder={t("posProducts.categories.searchPlaceholder")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${themeClasses.cardBg} border-b ${themeClasses.border}`}>
                  <tr>
                    <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>
                      {t("posProducts.categories.category")}
                    </th>
                    <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>
                      {t("posProducts.categories.products")}
                    </th>
                    <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>
                      {t("posProducts.categories.status")}
                    </th>
                    <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>
                      {t("posProducts.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCategories.map((category) => (
                    <tr
                      key={category.id}
                      className={`border-b ${themeClasses.border} ${themeClasses.tableRowHover} cursor-pointer`}
                      onClick={() => setSelectedCategory(category)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: `${category.color}20` }}
                          >
                            <i className={`${category.icon} text-xl`} style={{ color: category.color }}></i>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className={`font-medium ${themeClasses.text}`}>{category.name}</p>
                              {category.parentId && (
                                <span className={`text-xs px-2 py-0.5 rounded-full ${themeClasses.cardBg} ${themeClasses.textSecondary}`}>
                                  {t("posProducts.categories.subcategory")}
                                </span>
                              )}
                            </div>
                            <p className={`text-sm ${themeClasses.textSecondary}`}>{category.description}</p>
                          </div>
                        </div>
                      </td>
                      <td className={`px-4 py-3 text-center ${themeClasses.text}`}>{category.productCount}</td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            category.isActive ? "bg-emerald-500/20 text-emerald-500" : "bg-gray-500/20 text-gray-500"
                          }`}
                        >
                          {category.isActive ? t("posProducts.statusActive") : t("posProducts.statusInactive")}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <button aria-label={t("general.edit", { ns: "common" })} className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`}>
                            <i className="ri-edit-line"></i>
                          </button>
                          <button aria-label={t("general.delete", { ns: "common" })} className="p-2 rounded-lg hover:bg-red-500/10 text-red-500">
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4 h-fit`}>
          <h3 className={`font-semibold ${themeClasses.text} mb-4`}>{t("posProducts.categories.hierarchy")}</h3>
          <div className="space-y-2">
            {parentCategories.map((parent) => (
              <div key={parent.id}>
                <div className={`flex items-center gap-2 p-2 rounded-lg ${themeClasses.tableRowHover}`}>
                  <i className={`${parent.icon}`} style={{ color: parent.color }}></i>
                  <span className={themeClasses.text}>{parent.name}</span>
                  <span className={`text-xs ${themeClasses.textSecondary}`}>({parent.productCount})</span>
                </div>
                {getChildren(parent.id).map((child) => (
                  <div
                    key={child.id}
                    className={`flex items-center gap-2 p-2 pl-8 rounded-lg ${themeClasses.tableRowHover}`}
                  >
                    <i className={`${child.icon} text-sm`} style={{ color: child.color }}></i>
                    <span className={`text-sm ${themeClasses.text}`}>{child.name}</span>
                    <span className={`text-xs ${themeClasses.textSecondary}`}>({child.productCount})</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`${themeClasses.bg} rounded-2xl p-6 w-full max-w-md`}>
            <div className="flex justify-between items-center mb-4">
              <h2 className={`text-xl font-bold ${themeClasses.text}`}>{t("posProducts.categories.addCategory")}</h2>
              <button onClick={() => setShowCreateModal(false)} className={themeClasses.textSecondary}>
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>
                  {t("posProducts.categories.categoryName")}
                </label>
                <input
                  type="text"
                  className={`w-full px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>
                  {t("posProducts.categories.parentCategory")}
                </label>
                <select
                  className={`w-full px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                >
                  <option value="">{t("posProducts.categories.noParent")}</option>
                  {parentCategories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className={`flex-1 px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text}`}
                >
                  {t("common.cancel")}
                </button>
                <button className="flex-1 px-4 py-2 rounded-xl bg-primary-500 text-gray-900 font-medium">
                  {t("common.save")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
